// src/utils/PerfRecorder.ts

interface TaskMetric {
  count: number;
  totalMs: number;
  startTimeMap: Map<string, number>; // 支持并发调用，记录每个 ID 的开始时间
}

interface PerfRecorder {
  enter: (id: string) => void;
  quit: (id: string) => void;
  getStats: () => Record<string, { count: number; totalMs: number; avgMs: number }>;
  reset: () => void;
}

class PerfRecorderImpl {
  private metrics: Map<string, TaskMetric> = new Map();
  private activeTimers: Map<string, number> = new Map(); // 临时存储正在运行的任务开始时间

  constructor() {
    // 初始化默认数据
    this.reset();
  }

  /**
   * 标记开始
   */
  public enter(id: string): void {
    const now = performance.now();

    // 处理并发情况：如果同一个 ID 在上一次 quit 前又调用了 enter，
    // 这里可以选择覆盖时间或忽略。对于解包场景，通常是串行的，直接覆盖即可。
    // 如果是极度高频并发，可以使用数组栈，但为了性能，这里假设同 ID 串行或覆盖最新。
    this.activeTimers.set(id, now);
  }

  /**
   * 标记结束并统计
   */
  public quit(id: string): void {
    const startTime = this.activeTimers.get(id);
    if (startTime === undefined) {
      // 如果没有对应的 enter，可能是代码路径遗漏或异常，忽略或警告
      // console.warn(`[PerfRecorder] quit called for '${id}' without matching enter`);
      return;
    }

    const cost = performance.now() - startTime;
    this.activeTimers.delete(id);

    // 更新统计数据
    if (!this.metrics.has(id)) {
      this.metrics.set(id, { count: 0, totalMs: 0, startTimeMap: new Map() });
    }

    const metric = this.metrics.get(id)!;
    metric.count++;
    metric.totalMs += cost;
  }

  /**
   * 获取聚合报告
   */
  public getStats(): Record<
    string,
    { count: number; totalMs: number; avgMs: number }
    > {
    const result: Record<string, any> = {};
    this.metrics.forEach((val, key) => {
      result[key] = {
        count: val.count,
        totalMs: parseFloat(val.totalMs.toFixed(2)),
        avgMs:
          val.count > 0 ? parseFloat((val.totalMs / val.count).toFixed(3)) : 0,
      };
    });
    return result;
  }

  /**
   * 重置数据（诊断开始时调用）
   */
  public reset(): void {
    this.metrics.clear();
    this.activeTimers.clear();
  }
}

const noopPerfRecorder: PerfRecorder = {
  enter: (_id: string) => {},
  quit: (_id: string) => {},
  getStats: () => ({}),
  reset: () => {},
};

const win = window as any;

// 初始化并挂载到 window
if (!win.__PERF_RECORDER__) {
  try {
    const instance = new PerfRecorderImpl();
    win.__PERF_RECORDER__ = {
      enter: (id: string) => instance.enter(id),
      quit: (id: string) => instance.quit(id),
      getStats: () => instance.getStats(),
      reset: () => instance.reset(),
    };
  } catch (e) {
    console.error('初始化__PERF_RECORDER__失败', e);
    win.__PERF_RECORDER__ = noopPerfRecorder;
  }
}

if (!win.__PERF_RECORDER__) {
  win.__PERF_RECORDER__ = noopPerfRecorder;
}

export default win.__PERF_RECORDER__ as PerfRecorder;
